import { AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { SchemaProposal } from '@/services/api/schemaSessions'
import type { ValidationFinding } from '@/services/api/schemas'
import { FindingRow } from '../FindingRow'
import { ReviewDisclosure } from './ReviewDisclosure'

function findingSummary(finding: ValidationFinding, proposal: SchemaProposal) {
  if (finding.concept_id === 'proposal-provenance')
    return { key: 'provenance', target: finding.location?.replace('type_reasons.', '') ?? '' }
  if (finding.concept_id === 'source-content-coverage') {
    const source = proposal.sources?.find(file => finding.message.includes(file.document_id))
    return { key: coverageIssueKey(finding.message), target: source?.filename ?? '' }
  }
  return { key: 'validation', target: finding.message }
}

/** Map the validator's message onto a plain-language issue heading. */
function coverageIssueKey(message: string) {
  if (message.includes('mixes a source limitation')) return 'mixedCoverage'
  if (message.includes('can be kept as rows')) return 'tableCoverage'
  return 'contentCoverage'
}

function groupFindings(proposal: SchemaProposal) {
  const groups = new Map<string, Set<string>>()
  for (const finding of proposal.errors) {
    const summary = findingSummary(finding, proposal)
    const targets = groups.get(summary.key) ?? new Set<string>()
    if (summary.target) targets.add(summary.target)
    groups.set(summary.key, targets)
  }
  return groups
}

function FindingDetails({ findings }: { findings: ValidationFinding[] }) {
  const { t } = useTranslation()
  return (
    <ReviewDisclosure title={t('schemaAgent.review.technicalDetails')} count={findings.length}>
      <div className="space-y-2">
        {findings.map((finding, index) => (
          <FindingRow key={`${finding.location}:${index}`} finding={finding} concept={undefined} />
        ))}
      </div>
    </ReviewDisclosure>
  )
}

export function ProposalFindings({ proposal }: { proposal: SchemaProposal }) {
  const { t } = useTranslation()
  if (!proposal.errors.length) return null
  const groups = groupFindings(proposal)
  return (
    <div
      role="alert"
      className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 pt-4"
    >
      <p className="flex items-center gap-2 text-sm font-medium">
        <AlertCircle className="size-4 shrink-0 text-destructive" />
        {t('schemaAgent.review.needsRepair')}
      </p>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        {t('schemaAgent.review.errorsHint')}
      </p>
      <ul className="mt-4 space-y-4">
        {[...groups].map(([key, targets]) => (
          <li key={key}>
            <p className="text-sm font-medium">{t(`schemaAgent.review.issues.${key}`)}</p>
            {targets.size > 0 && (
              <p className="mt-1 break-words text-xs text-muted-foreground">
                {[...targets].join(', ')}
              </p>
            )}
          </li>
        ))}
      </ul>
      <FindingDetails findings={proposal.errors} />
    </div>
  )
}
